import React, { useMemo } from 'react';
import { Box, Typography, CircularProgress, Alert, Paper, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useQuery } from '@tanstack/react-query';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { usajiliApi } from '@services/api/usajili';
import { useUSAJILI } from '@hooks/useUSAJILI';
import { formatCompact } from '@utils/usajiliFormat';

/** Bar chart — New IDs / Replacements / Voters per county. Fills its parent's height. */
export const USAJILIToDateTab: React.FC = () => {
  const { filters, refetchKey } = useUSAJILI();
  const t = useTheme();
  // Phones: horizontal bars with county names down the side, so labels stay readable.
  const phone = useMediaQuery(t.breakpoints.down('sm'));
  const axisTick = { fontSize: 11, fill: t.palette.text.secondary };
  const tipStyle = { fontSize: 12, background: t.palette.background.paper, border: `1px solid ${t.palette.divider}`, color: t.palette.text.primary };
  const { data, isLoading, error } = useQuery({
    queryKey: ['usajili_todate', filters, refetchKey],
    queryFn: () => usajiliApi.toDate(filters),
  });

  // Drop the "Grand Total" pseudo-county from the chart — it visually
  // dwarfs the real counties.
  const chartData = useMemo(
    () => (data?.rows || []).filter((r) => r.county !== 'Grand Total'),
    [data]
  );

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <Typography sx={{ mb: 0.5, fontWeight: 700, fontSize: '0.8rem', letterSpacing: 0.3 }}>
        NEW IDs, REPLACEMENTS AND VOTERS REGISTERED PER COUNTY
      </Typography>
      <Paper data-pdf="usajili-todate" sx={{ p: 1, flex: 1, minHeight: 0, width: '100%', height: phone ? Math.max(260, chartData.length * 44 + 60) : undefined }}>
        {isLoading ? (
          <Box sx={{ height: '100%', display: 'grid', placeItems: 'center' }}><CircularProgress size={28} /></Box>
        ) : error ? (
          <Alert severity="error">{(error as any)?.error?.message || 'Failed to load To date chart'}</Alert>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            {phone ? (
            <BarChart data={chartData} layout="vertical" barCategoryGap="18%" margin={{ top: 4, right: 12, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={t.palette.divider} horizontal={false} />
              <XAxis type="number" tick={{ ...axisTick, fontSize: 10 }} tickFormatter={formatCompact} />
              <YAxis type="category" dataKey="county" interval={0} width={84} tick={{ ...axisTick, fontSize: 10 }} />
              <Tooltip contentStyle={tipStyle} cursor={{ fill: t.palette.action.hover }} />
              <Legend verticalAlign="top" iconSize={8} wrapperStyle={{ fontSize: 10, paddingBottom: 4 }} />
              <Bar dataKey="new_ids" name="New IDs" fill="#1F77B4" radius={[0, 2, 2, 0]} />
              <Bar dataKey="id_replacements" name="ID Replacements" fill="#FF7F0E" radius={[0, 2, 2, 0]} />
              <Bar dataKey="voters_registered" name="Voters Registered" fill="#2CA02C" radius={[0, 2, 2, 0]} />
            </BarChart>
            ) : (
            <BarChart data={chartData} margin={{ top: 4, right: 12, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={t.palette.divider} />
              <XAxis
                dataKey="county"
                interval={0}
                angle={-30}
                textAnchor="end"
                height={56}
                tick={{ ...axisTick, fontSize: 10 }}
              />
              <YAxis
                label={{ value: 'NO OF PERSONS', angle: -90, position: 'insideLeft', offset: 10, style: { fontWeight: 600, fontSize: 11, fill: t.palette.text.secondary } }}
                tick={axisTick}
                tickFormatter={formatCompact}
                width={62}
              />
              <Tooltip contentStyle={tipStyle} cursor={{ fill: t.palette.action.hover }} />
              <Legend verticalAlign="top" height={24} iconSize={10} wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="new_ids" name="New IDs" fill="#1F77B4" radius={[2, 2, 0, 0]} />
              <Bar dataKey="id_replacements" name="ID Replacements" fill="#FF7F0E" radius={[2, 2, 0, 0]} />
              <Bar dataKey="voters_registered" name="Voters Registered" fill="#2CA02C" radius={[2, 2, 0, 0]} />
            </BarChart>
            )}
          </ResponsiveContainer>
        )}
      </Paper>
    </Box>
  );
};
